import { useState, useRef, useEffect } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import MinimizeIcon from '@mui/icons-material/Remove';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { sendEmail, getEmailSuggestions, getComposerFromAddresses, getComposerClients } from '../../services/api';
import { toast } from '../toast';
import {
  MAX_ATTACHMENTS,
  MAX_PER_FILE_BYTES,
  MAX_TOTAL_BYTES,
  ALLOWED_EXTENSIONS,
  formatBytes,
  getFileIcon,
} from './emailAttachmentUtils';

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    ['link'],
    ['clean'],
  ],
};

const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'list', 'bullet', 'indent', 'link',
];

function EmailAutocompleteInput({ label, chips, onChange, suggestions }) {
  const [inputValue, setInputValue] = useState('');

  const commitInput = (val) => {
    const email = val.trim().replace(/[, ]+$/, '');
    if (email && !chips.includes(email)) onChange([...chips, email]);
    setInputValue('');
  };

  return (
    <Autocomplete
      multiple
      freeSolo
      disableClearable
      size="small"
      options={suggestions}
      getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.email)}
      isOptionEqualToValue={(opt, val) =>
        opt.email === (typeof val === 'string' ? val : val.email)
      }
      filterOptions={(opts, { inputValue: q }) => {
        if (!q) return [];
        const lower = q.toLowerCase();
        return opts.filter(
          (o) =>
            o.email.toLowerCase().includes(lower) ||
            (o.label && o.label.toLowerCase().includes(lower))
        );
      }}
      value={chips}
      inputValue={inputValue}
      onInputChange={(_, val, reason) => {
        if (reason !== 'input') return;
        if (val.endsWith(',') || val.endsWith(' ')) {
          commitInput(val);
        } else {
          setInputValue(val);
        }
      }}
      onChange={(_, newValue) => {
        const emails = newValue
          .map((v) => (typeof v === 'string' ? v.trim() : v.email))
          .filter(Boolean);
        onChange(emails);
        setInputValue('');
      }}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const email = typeof option === 'string' ? option : option.email;
          return (
            <Chip
              key={email}
              label={email}
              size="small"
              {...getTagProps({ index })}
              deleteIcon={<CloseIcon sx={{ fontSize: '13px !important' }} />}
              sx={{ fontSize: 12, height: 24 }}
            />
          );
        })
      }
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.email}>
          <Stack spacing={0}>
            <Typography variant="body2">{option.email}</Typography>
            {option.label && (
              <Typography variant="caption" color="text.secondary">{option.label}</Typography>
            )}
          </Stack>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder={chips.length === 0 ? 'Type email and press Enter…' : ''}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mr: 0.5, userSelect: 'none', flexShrink: 0 }}
                >
                  {label}
                </Typography>
                {params.InputProps.startAdornment}
              </>
            ),
          }}
          onKeyDown={(e) => {
            if (e.key === 'Tab' && inputValue) {
              e.preventDefault();
              commitInput(inputValue);
            } else if (e.key === 'Backspace' && !inputValue && chips.length) {
              onChange(chips.slice(0, -1));
            }
          }}
          onBlur={() => { if (inputValue) commitInput(inputValue); }}
        />
      )}
    />
  );
}

export default function ComposeEmailPopup({ onClose, onSent }) {
  const fileInputRef = useRef(null);
  const quillRef = useRef(null);
  const scrollBoxRef = useRef(null);

  const [from, setFrom] = useState('');
  const [fromName, setFromName] = useState('BillerBay Transmit');
  const [fromAddresses, setFromAddresses] = useState([]);
  const [to, setTo] = useState([]);
  const [cc, setCc] = useState([]);
  const [showCc, setShowCc] = useState(true);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [minimized, setMinimized] = useState(false);
  const [sending, setSending] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [templates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => {
    getEmailSuggestions()
      .then((res) => setSuggestions(res.data))
      .catch(() => {});
    getComposerFromAddresses()
      .then((res) => {
        const addresses = res.data.from_addresses ?? [];
        setFromAddresses(addresses);
        if (addresses.length) setFrom(addresses[0]);
      })
      .catch(() => {});
    getComposerClients()
      .then((res) => setClients(res.data.clients ?? []))
      .catch(() => {});
  }, []);

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    if (client) {
      setTo(client.primary_email ? [client.primary_email] : []);
      setCc(client.secondary_emails || []);
      if (client.secondary_emails && client.secondary_emails.length) setShowCc(true);
    }
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    if (template) {
      setSubject(template.subject || '');
      // The template is a complete HTML email sent as-is by the backend; the
      // body isn't composed here (and full HTML can't round-trip through Quill).
      setBody('');
      // Templates carry their own sender identity.
      if (template.from_email) setFrom(template.from_email);
      if (template.from_name) setFromName(template.from_name);
    } else {
      // Cleared — revert to composer defaults.
      setFrom(fromAddresses[0] || '');
      setFromName('BillerBay Transmit');
    }
  };

  const totalAttachmentBytes = attachedFiles.reduce((sum, a) => sum + a.file.size, 0);
  const attachmentOverLimit = totalAttachmentBytes > MAX_TOTAL_BYTES;
  const canSend = from.trim() && to.length > 0 && subject.trim() && !sending && !attachmentOverLimit;

  const handleBodyChange = (value) => {
    setBody(value);
    requestAnimationFrame(() => {
      if (!quillRef.current || !scrollBoxRef.current) return;
      const quill = quillRef.current.getEditor();
      const sel = quill.getSelection();
      if (!sel) return;
      const bounds = quill.getBounds(sel.index, sel.length);
      const editorEl = quill.root;
      const scrollBox = scrollBoxRef.current;
      const editorRect = editorEl.getBoundingClientRect();
      const boxRect = scrollBox.getBoundingClientRect();
      const cursorTop = editorRect.top - boxRect.top + scrollBox.scrollTop + bounds.top;
      const cursorBottom = cursorTop + bounds.height;
      if (cursorBottom > scrollBox.scrollTop + scrollBox.clientHeight - 16) {
        scrollBox.scrollTop = cursorBottom - scrollBox.clientHeight + 16;
      } else if (cursorTop < scrollBox.scrollTop + 8) {
        scrollBox.scrollTop = cursorTop - 8;
      }
    });
  };

  const handleFileSelect = (e) => {
    const incoming = Array.from(e.target.files || []);
    e.target.value = '';
    const skipped = [];
    const duplicateWarnings = [];
    const accepted = incoming.reduce((current, file) => {
      const ext = file.name.includes('.') ? file.name.split('.').pop().toLowerCase() : '';
      if (!ALLOWED_EXTENSIONS.has(ext)) { skipped.push(`'${file.name}' — unsupported type`); return current; }
      if (file.size > MAX_PER_FILE_BYTES) { skipped.push(`'${file.name}' — exceeds 10 MB`); return current; }
      if (current.length + 1 > MAX_ATTACHMENTS) { skipped.push(`'${file.name}' — max ${MAX_ATTACHMENTS} attachments`); return current; }
      const newTotal = current.reduce((s, a) => s + a.file.size, 0) + file.size;
      if (newTotal > MAX_TOTAL_BYTES) { skipped.push(`'${file.name}' — would exceed 15 MB total`); return current; }
      const isDuplicate = current.some((a) => a.file.name === file.name && a.file.size === file.size);
      if (isDuplicate) duplicateWarnings.push(`'${file.name}' looks like a duplicate — already attached.`);
      return [...current, { id: `${file.name}-${file.size}-${Date.now()}`, file }];
    }, attachedFiles);
    setAttachedFiles(accepted);
    if (skipped.length) toast.error(skipped.join('\n'));
    if (duplicateWarnings.length) toast.warning(duplicateWarnings.join('\n'));
  };

  const removeAttachment = (id) => setAttachedFiles((prev) => prev.filter((a) => a.id !== id));

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('from_email', from.trim());
      formData.append('from_name', fromName.trim());
      formData.append('recipient_emails', to.join(','));
      formData.append('cc_list', cc.join(','));
      formData.append('subject', subject);
      formData.append('body', body);
      if (selectedTemplate) formData.append('template_id', selectedTemplate.id);
      attachedFiles.forEach(({ file }) => formData.append('attachments', file));
      await sendEmail(formData);
      toast.success('Email sent successfully');
      if (onSent) onSent();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error('Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
    <Paper
      elevation={8}
      sx={{
        position: 'fixed', bottom: 0, right: 32, width: 520, maxHeight: 600,
        zIndex: 1300, borderRadius: '8px 8px 0 0', overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2, py: 1, bgcolor: 'grey.800', color: 'common.white',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer', userSelect: 'none', flexShrink: 0,
        }}
        onClick={() => setMinimized((v) => !v)}
      >
        <Typography variant="subtitle2" fontWeight={600}>New Message</Typography>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={minimized ? 'Expand' : 'Minimize'}>
            <IconButton size="small" sx={{ color: 'common.white' }} onClick={(e) => { e.stopPropagation(); setMinimized((v) => !v); }}>
              <MinimizeIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton size="small" sx={{ color: 'common.white' }} onClick={(e) => { e.stopPropagation(); onClose(); }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Scrollable body */}
      <Collapse in={!minimized}>
        <Box ref={scrollBoxRef} sx={{ overflowY: 'auto', maxHeight: 490 }}>
          {/* CC toggle row */}
          <Box sx={{ px: 2, pt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="small" variant={showCc ? 'contained' : 'text'} disableElevation
              onClick={() => setShowCc((v) => !v)}
              sx={{ minWidth: 0, px: 1.5, fontSize: 12 }}
            >
              CC
            </Button>
          </Box>

          <Stack sx={{ px: 2, pb: 1 }} spacing={1}>
            <Autocomplete
              freeSolo
              size="small"
              options={[]}
              value={fromName}
              onChange={(_, value) => setFromName(value || '')}
              onInputChange={(_, value) => setFromName(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Sender name"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <Typography
                        variant="body2" color="text.secondary"
                        sx={{ width: 64, flexShrink: 0, userSelect: 'none' }}
                      >
                        From name
                      </Typography>
                    ),
                  }}
                />
              )}
            />
            <Autocomplete
              freeSolo
              size="small"
              options={fromAddresses}
              value={from}
              onChange={(_, value) => setFrom(value || '')}
              onInputChange={(_, value) => setFrom(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Sender email"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <Typography
                        variant="body2" color="text.secondary"
                        sx={{ width: 64, flexShrink: 0, userSelect: 'none' }}
                      >
                        From email
                      </Typography>
                    ),
                  }}
                />
              )}
            />
            {templates.length > 0 && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Autocomplete
                  size="small"
                  sx={{ flex: 1 }}
                  options={templates}
                  value={selectedTemplate}
                  onChange={(_, value) => handleSelectTemplate(value)}
                  getOptionLabel={(opt) => opt.name || ''}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  renderInput={(params) => (
                    <TextField {...params} size="small" placeholder="Use a template (optional)" />
                  )}
                />
              </Stack>
            )}
            {clients.length > 0 && (
              <Autocomplete
                size="small"
                options={clients}
                value={selectedClient}
                onChange={(_, value) => handleSelectClient(value)}
                getOptionLabel={(opt) => opt.name || ''}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                renderOption={(props, opt) => (
                  <Box component="li" {...props} key={opt.id}>
                    <Stack spacing={0}>
                      <Typography variant="body2">{opt.name}</Typography>
                      {opt.primary_email && (
                        <Typography variant="caption" color="text.secondary">{opt.primary_email}</Typography>
                      )}
                    </Stack>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField {...params} size="small" placeholder="Select a client (optional)" />
                )}
              />
            )}
            <EmailAutocompleteInput label="To" chips={to} onChange={setTo} suggestions={suggestions} />
            <Collapse in={showCc} unmountOnExit>
              <EmailAutocompleteInput label="CC" chips={cc} onChange={setCc} suggestions={suggestions} />
            </Collapse>
            <TextField
              fullWidth size="small" placeholder="Subject"
              value={subject} onChange={(e) => setSubject(e.target.value)}
              disabled={!!selectedTemplate}
            />
          </Stack>

          <Divider />

          {selectedTemplate ? (
            <Box
              sx={{
                m: 2, p: 2, minHeight: 180, borderRadius: 1,
                border: '1px dashed', borderColor: 'divider', bgcolor: 'grey.50',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', textAlign: 'center', gap: 1.5,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                The “{selectedTemplate.name}” template will be sent exactly as designed.
                Its content can’t be edited here.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                px: 2, pt: 0.5, pb: 0,
                '& .ql-container': { border: 'none', fontSize: 13, fontFamily: 'inherit' },
                '& .ql-toolbar': { border: 'none', borderBottom: '1px solid', borderColor: 'divider', px: 0 },
                '& .ql-editor': {
                  minHeight: 220, px: 1,
                  '&.ql-blank::before': { fontStyle: 'normal', color: 'text.disabled' },
                },
              }}
            >
              <ReactQuill
                ref={quillRef} theme="snow" value={body}
                onChange={handleBodyChange} modules={QUILL_MODULES}
                formats={QUILL_FORMATS} placeholder="Write your message here…"
              />
            </Box>
          )}

          {attachedFiles.length > 0 && (
            <Box sx={{ px: 2, pt: 1.5, pb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {attachedFiles.map(({ id, file }) => (
                <Chip
                  key={id}
                  icon={getFileIcon(file.name, { fontSize: 16 })}
                  label={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography variant="caption" noWrap sx={{ maxWidth: 140 }}>{file.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{formatBytes(file.size)}</Typography>
                    </Stack>
                  }
                  onDelete={() => removeAttachment(id)}
                  deleteIcon={<CloseIcon sx={{ fontSize: '13px !important' }} />}
                  size="small" variant="outlined"
                  sx={{ fontSize: 12, height: 28, maxWidth: 260 }}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Send / Attach bar */}
        <Divider />
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Button
            variant="contained" disableElevation size="small"
            startIcon={<SendIcon />} disabled={!canSend} onClick={handleSend}
          >
            {sending ? 'Sending…' : 'Send'}
          </Button>
          <Tooltip title={`Attach file (max ${MAX_ATTACHMENTS} files, 10 MB each, 15 MB total)`}>
            <IconButton size="small" onClick={() => fileInputRef.current?.click()} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <AttachFileIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {attachedFiles.length > 0 && (
            <Typography variant="caption" color={attachmentOverLimit ? 'error' : 'text.secondary'} sx={{ ml: 0.5 }}>
              {formatBytes(totalAttachmentBytes)} / 15 MB
            </Typography>
          )}
          <input
            ref={fileInputRef} type="file" multiple hidden
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp"
            onChange={handleFileSelect}
          />
        </Box>
      </Collapse>
    </Paper>

    </>
  );
}
